import { requestMicrophone } from "@/lib/audio-input"

/** A stable output track keeps MediaRecorder and live capture running across device changes. */
export async function createMicrophoneInput(
  constraints: MediaTrackConstraints = {},
  options?: AudioContextOptions
) {
  let microphone = await requestMicrophone(constraints)
  let context: AudioContext | undefined
  try {
    context = new AudioContext(options)
    if (context.state === "suspended") await context.resume()
    const destination = context.createMediaStreamDestination()
    let source = context.createMediaStreamSource(microphone)
    source.connect(destination)
    let stopped = false
    let generation = 0
    const audioContext = context

    return {
      context: audioContext,
      stream: destination.stream,
      get label() {
        return microphone.getAudioTracks()[0]?.label ?? ""
      },
      async switchMicrophone(deviceId: string | null) {
        const mine = ++generation
        // Explicit changes must fail visibly, without the startup fallback.
        const next = await navigator.mediaDevices.getUserMedia({
          audio: deviceId
            ? { ...constraints, deviceId: { exact: deviceId } }
            : constraints,
        })
        let nextSource: MediaStreamAudioSourceNode | undefined
        try {
          if (stopped || mine !== generation)
            throw new Error("Microphone switch cancelled")
          nextSource = audioContext.createMediaStreamSource(next)
          nextSource.connect(destination)
        } catch (error) {
          nextSource?.disconnect()
          next.getTracks().forEach((track) => track.stop())
          throw error
        }
        source.disconnect()
        microphone.getTracks().forEach((track) => track.stop())
        microphone = next
        source = nextSource
      },
      stop() {
        if (stopped) return
        stopped = true
        ++generation
        source.disconnect()
        microphone.getTracks().forEach((track) => track.stop())
        destination.stream.getTracks().forEach((track) => track.stop())
        void audioContext.close()
      },
    }
  } catch (error) {
    microphone.getTracks().forEach((track) => track.stop())
    void context?.close()
    throw error
  }
}

export type MicrophoneInput = Awaited<ReturnType<typeof createMicrophoneInput>>
